import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';

const HEAT_GRADIENT = {
  0.0: '#313695',
  0.1: '#4575b4',
  0.2: '#74add1',
  0.35: '#abd9e9',
  0.5: '#fee090',
  0.65: '#fdae61',
  0.8: '#f46d43',
  0.9: '#d73027',
  1.0: '#a50026',
};

export default function HeatMapLayer({ points, options = {}, onStatsReady }) {
  const map = useMap();
  const prevStatsRef = useRef(null);

  useEffect(() => {
    if (!points || points.length === 0) return;

    const intensities = points.map(p => p[2] || 0);
    const maxIntensity = Math.max(...intensities);
    const minIntensity = Math.min(...intensities.filter(v => v > 0));

    // Report stats for the legend (only if values changed to avoid re-render loop)
    if (onStatsReady) {
      const newMin = minIntensity || 0;
      const newMax = maxIntensity || 0;
      if (!prevStatsRef.current || prevStatsRef.current.min !== newMin || prevStatsRef.current.max !== newMax) {
        prevStatsRef.current = { min: newMin, max: newMax };
        onStatsReady({ min: newMin, max: newMax });
      }
    }

    // Normalize to 0-1 with a sqrt scale for better visual spread
    const normalized = points.map(p => {
      const raw = maxIntensity > 0 ? (p[2] || 0) / maxIntensity : 0;
      return [p[0], p[1], Math.sqrt(raw)];
    });

    const heat = L.heatLayer(normalized, {
      radius: options.radius || 25,
      blur: options.blur || 15,
      maxZoom: options.maxZoom || 18,
      max: 1.0,
      minOpacity: options.minOpacity || 0.35,
      gradient: options.gradient || HEAT_GRADIENT,
    });

    heat.addTo(map);
    return () => {
      map.removeLayer(heat);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, points]);

  return null;
}

export { HEAT_GRADIENT };
